import React, { useState } from 'react';
import T from '../styles/theme';
import { Btn, Checkbox } from '../components/UI';
import Icon from '../components/Icon';
import { useStore } from '../data/store';
import apiFetch from '../data/api';

/* ── Compose Modal ──
 * Shared email composer used by EmailPage (manual compose) and Pipeline
 * Doctor (AI-generated draft preview + edit + send).
 *
 * Props:
 *   onClose        — cancel / close handler (required)
 *   onSent         — (result) => void, called after successful send
 *   replyTo        — { from, subject } to seed "RE:" + recipient
 *   forwardEmail   — { from, subject, body|preview } to seed "FW:" + quoted body
 *   contact        — { email, ... } to seed the To field
 *   template       — { subject, body } to pre-fill on mount (use for AI drafts)
 *   templates      — [{ id, name, subject, body }] catalog for the picker; if
 *                    empty or omitted, the Templates toggle + picker UI are hidden
 */
function ComposeModal({ onClose, replyTo, forwardEmail, contact, onSent, template, templates = [] }) {
  const { contacts: allContacts, logEmail, currentUserId } = useStore();
  const [to, setTo] = useState(replyTo ? replyTo.from : contact ? contact.email : "");
  const [subject, setSubject] = useState(
    replyTo ? "RE: " + replyTo.subject :
    forwardEmail ? "FW: " + forwardEmail.subject :
    template ? template.subject : ""
  );
  const [body, setBody] = useState(
    forwardEmail ? "\n\n---------- Forwarded message ----------\nFrom: " + forwardEmail.from + "\nSubject: " + forwardEmail.subject + "\n\n" + (forwardEmail.body || forwardEmail.preview) :
    template ? template.body : ""
  );
  const [showTemplates, setShowTemplates] = useState(false);
  const [schedule, setSchedule] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const filteredContacts = allContacts.filter(c =>
    to.length > 0 && !to.includes("@") &&
    (c.fn + " " + c.ln + " " + c.email + " " + c.co).toLowerCase().includes(to.toLowerCase())
  );

  const selectContact = (c) => {
    setTo(c.email);
    setShowSuggestions(false);
  };

  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [trackOpens, setTrackOpens] = useState(true);
  const [trackClicks, setTrackClicks] = useState(true);
  const fileInputRef = React.useRef(null);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      if (file.size > 10 * 1024 * 1024) { setSendError("File too large (max 10MB): " + file.name); return; }
      const reader = new FileReader();
      reader.onload = () => {
        setAttachments(prev => [...prev, { name: file.name, type: file.type || "application/octet-stream", size: file.size, data: reader.result.split(",")[1] }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const removeAttachment = (idx) => setAttachments(prev => prev.filter((_, i) => i !== idx));

  const handleSend = async (scheduledTime) => {
    if (!to || !subject) { setSendError("To and Subject are required"); return; }
    setSending(true); setSendError(null);

    // Find contact by email
    const ct = allContacts.find(c => c.email === to);

    try {
      const payload = {
        to: to,
        subject: subject,
        body: body,
        contactId: ct?.id || null,
        userId: currentUserId,
        orgId: window.__coreflow_org_id,
        trackOpens,
        trackClicks,
        attachments: attachments.length > 0 ? attachments : undefined,
      };
      if (scheduledTime) payload.scheduledAt = scheduledTime;

      const resp = await apiFetch("/api/email/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await resp.json();

      if (result.success) {
        console.log("[CoreFlow] Email sent:", result.id);
        // Also log locally
        if (ct) logEmail({ contactId: ct.id, title: subject || "Email sent", notes: "To: " + to + (body ? " — " + body.slice(0, 80) : "") });
        if (onSent) onSent(result);
        onClose();
      } else {
        setSendError(result.error || "Send failed");
      }
    } catch (err) {
      console.error("[CoreFlow] Email error:", err);
      setSendError("Network error — check console");
    } finally {
      setSending(false);
    }
  };

  const applyTemplate = (tmpl) => {
    setSubject(tmpl.subject);
    setBody(tmpl.body);
    setShowTemplates(false);
  };

  const inputStyle = {
    width: "100%", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8,
    padding: "8px 10px", color: "#FAFAFA", fontSize: 12, outline: "none", fontFamily: "inherit",
  };

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000,
    }}>
      <div style={{
        width: window.innerWidth < 768 ? "100%" : 680, maxHeight: window.innerWidth < 768 ? "100%" : "85vh", background: "#2C2E33", border: window.innerWidth < 768 ? "none" : "1.5px solid rgba(217,219,221,0.12)",
        borderRadius: window.innerWidth < 768 ? 0 : 8, display: "flex", flexDirection: "column", overflow: "auto",
      }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderBottom: "1px solid " + T.bdr }}>
          <span style={{ fontSize: 14, fontWeight: 400, color: T.txt }}>
            {replyTo ? "Reply" : forwardEmail ? "Forward" : "New Email"}
          </span>
          <div style={{ display: "flex", gap: 6 }}>
            {templates.length > 0 && (
              <button onClick={() => setShowTemplates(!showTemplates)} style={{
                padding: "4px 10px", borderRadius: 3, border: "1px solid " + T.bdr,
                background: showTemplates ? T.blue + "15" : "transparent",
                color: showTemplates ? T.blue : T.tx3, fontSize: 10, fontWeight: 400, cursor: "pointer",
              }}>Templates</button>
            )}
            <button onClick={onClose} style={{
              background: "none", border: "none", color: T.tx3, fontSize: 18, cursor: "pointer",
            }}><Icon name="x" size={14} color={T.tx3} /></button>
          </div>
        </div>

        {/* Template picker */}
        {templates.length > 0 && showTemplates && (
          <div style={{ padding: "8px 16px", borderBottom: "1px solid " + T.bdr, background: T.sf }}>
            <div style={{ fontSize: 9, color: T.tx3, fontWeight: 400, letterSpacing: ".08em", marginBottom: 6 }}>SELECT TEMPLATE</div>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {templates.map(t => (
                <button key={t.id} onClick={() => applyTemplate(t)} style={{
                  padding: "5px 10px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)",
                  background: "transparent", border: "1px solid rgba(217,219,221,0.12)", color: "#FAFAFA", fontSize: 11, fontWeight: 400, cursor: "pointer", fontFamily: "inherit",
                }}>{t.name}</button>
              ))}
            </div>
          </div>
        )}

        {/* Fields */}
        <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ position: "relative" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 11, color: T.tx3, minWidth: 50 }}>To:</span>
              <input value={to} onChange={e => { setTo(e.target.value); setShowSuggestions(true); }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                placeholder="Search contacts or type email..." style={inputStyle} />
            </div>
            {/* Contact autocomplete dropdown */}
            {showSuggestions && to.length > 0 && !to.includes("@") && filteredContacts.length > 0 && (
              <div style={{
                position: "absolute", top: "100%", left: 58, right: 0, marginTop: 2,
                background: "#33363D", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8,
                boxShadow: "0 8px 24px rgba(0,0,0,0.4)", zIndex: 10, maxHeight: 200, overflow: "auto",
              }}>
                {filteredContacts.map(ct => (
                  <div key={ct.id} onMouseDown={() => selectContact(ct)} style={{
                    display: "flex", alignItems: "center", gap: 8, padding: "8px 12px",
                    cursor: "pointer", borderBottom: "1px solid " + T.bdr + "40",
                    transition: "background 0.1s",
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = T.sf}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: T.blue + "15", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 400, color: T.blue }}>{ct.fn[0]}{ct.ln[0]}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 400, color: T.txt }}>{ct.fn} {ct.ln}</div>
                      <div style={{ fontSize: 10, color: T.tx3 }}>{ct.email} · {ct.co}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* Show all contacts when field is focused but empty */}
            {showSuggestions && to.length === 0 && (
              <div style={{
                position: "absolute", top: "100%", left: 58, right: 0, marginTop: 2,
                background: "#33363D", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8,
                boxShadow: "0 8px 24px rgba(0,0,0,0.4)", zIndex: 10, maxHeight: 200, overflow: "auto",
              }}>
                <div style={{ padding: "6px 12px", fontSize: 9, color: T.tx3, fontWeight: 400, letterSpacing: ".06em" }}>CONTACTS</div>
                {allContacts.map(ct => (
                  <div key={ct.id} onMouseDown={() => selectContact(ct)} style={{
                    display: "flex", alignItems: "center", gap: 8, padding: "6px 12px",
                    cursor: "pointer", borderBottom: "1px solid " + T.bdr + "40",
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = T.sf}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <div style={{ width: 24, height: 24, borderRadius: 8, background: T.blue + "15", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 400, color: T.blue }}>{ct.fn[0]}{ct.ln[0]}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 11, fontWeight: 400, color: T.txt }}>{ct.fn} {ct.ln}</div>
                      <div style={{ fontSize: 9, color: T.tx3 }}>{ct.email}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 11, color: T.tx3, minWidth: 50 }}>Subject:</span>
            <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Email subject" style={inputStyle} />
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, padding: "0 16px 12px" }}>
          <textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Write your email..."
            style={{ ...inputStyle, height: 250, resize: "none", lineHeight: 1.6, fontFamily: "inherit" }} />
        </div>

        {/* AI Suggestions */}
        {body.length > 20 && !forwardEmail && (
          <div style={{ margin: "0 16px 8px", padding: "8px 10px", borderRadius: 4, background: T.blue + "06", border: "1px solid " + T.blue + "12" }}>
            <div style={{ fontSize: 9, color: T.blue, fontWeight: 400, letterSpacing: ".08em", marginBottom: 3 }}><Icon name="ai" size={11} color={T.blue} /> CORE SUGGESTS</div>
            <div style={{ fontSize: 11, color: T.tx2 }}>Consider adding a specific call-to-action with available meeting times to increase response rate by ~35%.</div>
          </div>
        )}

        {/* Send error */}
        {sendError && (
          <div style={{ padding: "6px 16px", background: "#E53935" + "10", borderTop: "1px solid " + "#E53935" + "30" }}>
            <span style={{ fontSize: 11, color: "#E53935" }}>{sendError}</span>
          </div>
        )}

        {/* Footer */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px", borderTop: "1px solid " + T.bdr }}>
          <div style={{ display: "flex", gap: 6 }}>
            <Btn primary onClick={() => handleSend()} disabled={sending}>{sending ? "Sending..." : "Send"}</Btn>
            <button onClick={() => setSchedule(!schedule)} style={{
              padding: "7px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)",
              background: schedule ? "rgba(64,89,173,0.15)" : "transparent",
              color: "#FAFAFA", fontSize: 11, fontWeight: 400, cursor: "pointer", fontFamily: "inherit",
            }}>Schedule</button>
            <button onClick={() => fileInputRef.current?.click()} style={{ padding: "7px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: attachments.length > 0 ? "rgba(64,89,173,0.15)" : "transparent", color: "#FAFAFA", fontSize: 11, cursor: "pointer" }}>Attach{attachments.length > 0 ? ` (${attachments.length})` : ""}</button>
            <input ref={fileInputRef} type="file" multiple onChange={handleFileSelect} style={{ display: "none" }} />
          </div>
          {attachments.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "6px 0" }}>
              {attachments.map((a, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 4, padding: "3px 8px", borderRadius: 4, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.12)", fontSize: 10, color: "#FAFAFA" }}>
                  <span>{a.name}</span>
                  <span style={{ color: T.tx3 }}>({(a.size / 1024).toFixed(0)}KB)</span>
                  <span onClick={() => removeAttachment(i)} style={{ cursor: "pointer", marginLeft: 2, fontWeight: 400, color: T.tx3 }}>×</span>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: T.tx3, cursor: "pointer" }}>
              <Checkbox checked={trackOpens} onChange={() => setTrackOpens(v => !v)} /> Track opens
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: T.tx3, cursor: "pointer" }}>
              <Checkbox checked={trackClicks} onChange={() => setTrackClicks(v => !v)} /> Track clicks
            </label>
          </div>
        </div>

        {schedule && (
          <div style={{ padding: "10px 16px", borderTop: "1px solid " + T.bdr, background: "rgba(255,255,255,0.02)" }}>
            <div style={{ fontSize: 9, color: T.tx3, fontWeight: 400, letterSpacing: ".08em", marginBottom: 8 }}>SCHEDULE SEND</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
              {[
                { label: "Tomorrow 8AM", getTime: () => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(8,0,0,0); return d.toISOString(); } },
                { label: "Tomorrow 10AM", getTime: () => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(10,0,0,0); return d.toISOString(); } },
                { label: "Tomorrow 2PM", getTime: () => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(14,0,0,0); return d.toISOString(); } },
                { label: "Monday 9AM", getTime: () => { const d = new Date(); const day = d.getDay(); const diff = day <= 1 ? 1 - day : 8 - day; d.setDate(d.getDate() + diff); d.setHours(9,0,0,0); return d.toISOString(); } },
                { label: "In 2 Hours", getTime: () => new Date(Date.now() + 2 * 3600000).toISOString() },
                { label: "In 4 Hours", getTime: () => new Date(Date.now() + 4 * 3600000).toISOString() },
                { label: "Next Wednesday 9AM", getTime: () => { const d = new Date(); const day = d.getDay(); const diff = day <= 3 ? 3 - day : 10 - day; d.setDate(d.getDate() + diff); d.setHours(9,0,0,0); return d.toISOString(); } },
              ].map(t => (
                <button key={t.label} onClick={() => handleSend(t.getTime())} style={{
                  padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)",
                  background: "transparent", color: "#FAFAFA", fontSize: 10, fontWeight: 400, cursor: "pointer", fontFamily: "inherit",
                }}>{t.label}</button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span style={{ fontSize: 10, color: T.tx3 }}>Custom:</span>
              <input type="datetime-local" id="cf-schedule-custom"
                style={{ padding: "5px 10px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.04)", color: "#FAFAFA", fontSize: 11, fontFamily: "inherit", outline: "none", colorScheme: "dark" }} />
              <button onClick={() => { const v = document.getElementById("cf-schedule-custom")?.value; if (v) handleSend(new Date(v).toISOString()); else setSendError("Pick a date and time first"); }} style={{
                padding: "5px 14px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)",
                background: "#4059AD", color: "#FAFAFA", fontSize: 10, fontWeight: 400, cursor: "pointer", fontFamily: "inherit",
              }}>Schedule</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ComposeModal;
